import { ethers } from 'ethers'
import { t } from '../i18n'

const ENDPOINT = (process.env.VUE_APP_GOLDSKY_ENDPOINT || '').trim()
const CONTRACT = process.env.VUE_APP_CONTRACT_ADDRESS || ''
const EXPLORER = Number(process.env.VUE_APP_CHAIN_ID || 56) === 97 ? 'https://testnet.bscscan.com' : 'https://bscscan.com'
const ACCOUNT_FIELDS = 'id deposits directPerformance directCount boundAt firstDepositedAt'
const META = '_meta { hasIndexingErrors }'

async function queryIndexer(query, variables = {}) {
  if (!ENDPOINT) throw new Error(t('error.indexerNotConfigured'))
  const response = await fetch(ENDPOINT, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ query, variables }),
  })
  const payload = await response.json()
  if (!response.ok || payload.errors?.length || !payload.data || payload.data._meta?.hasIndexingErrors) {
    throw new Error(t('error.apiUnavailable'))
  }
  return payload.data
}

const amount = value => ethers.utils.formatEther(value || '0')
const date = value => value == null ? null : new Date(Number(value) * 1000).toISOString()
const member = value => value ? ({
  address: value.id,
  performance: amount(value.deposits),
  directPerformance: amount(value.directPerformance),
  directCount: value.directCount,
  boundAt: date(value.boundAt),
  firstPurchasedAt: date(value.firstDepositedAt),
}) : null

export async function getMember(address) {
  const data = await queryIndexer(`query Member($id: ID!) {
    saleAccount(id: $id) { ${ACCOUNT_FIELDS} }
    ${META}
  }`, { id: address.toLowerCase() })
  return member(data.saleAccount)
}

export async function getTeamPerformance(address) {
  const data = await queryIndexer(`query ReferralTeam($id: ID!) {
    saleAccount(id: $id) { teamPerformance }
    ${META}
  }`, { id: address.toLowerCase() })
  return amount(data.saleAccount?.teamPerformance)
}

export async function getDirectMembers(address, { page = 1, pageSize = 10 } = {}) {
  const data = await queryIndexer(`query Directs($id: ID!, $referrer: String!, $first: Int!, $skip: Int!) {
    saleAccount(id: $id) { directCount }
    saleAccounts(where: {referrer: $referrer}, orderBy: id, orderDirection: asc, first: $first, skip: $skip) {
      ${ACCOUNT_FIELDS}
    }
    ${META}
  }`, { id: address.toLowerCase(), referrer: address.toLowerCase(), first: pageSize, skip: (page - 1) * pageSize })
  return { list: data.saleAccounts.map(member), total: data.saleAccount?.directCount || 0, page, pageSize }
}

export async function getIdoOverview() {
  const data = await queryIndexer(`query Overview {
    saleStats(id: "sale") { contractAddress totalDeposited participantCount }
    ${META}
  }`)
  const stats = data.saleStats
  if (stats && CONTRACT && stats.contractAddress.toLowerCase() !== CONTRACT.toLowerCase()) {
    throw new Error(t('error.indexerContractMismatch'))
  }
  return {
    totalRaised: amount(stats?.totalDeposited),
    participantCount: stats?.participantCount || 0,
    contractAddress: CONTRACT,
    explorerUrl: EXPLORER,
  }
}

export async function getLeaderboard(address, { page = 1, pageSize = 10 } = {}) {
  const empty = { list: [], total: 0, page: 1, pageSize }
  if (!address) return empty
  const snapshot = await queryIndexer(`query TeamSnapshot($id: ID!) {
    saleAccount(id: $id) { deposits rankKey }
    saleStats(id: "sale") { participantCount }
    _meta { hasIndexingErrors block { number } }
  }`, { id: address.toLowerCase() })
  if (!snapshot.saleAccount || snapshot.saleAccount.deposits === '0') return empty

  const block = snapshot._meta.block.number
  const rankKey = snapshot.saleAccount.rankKey
  let cursor = rankKey
  let total = 0
  let batchSize
  // Count with a cursor at one indexed block so pagination and global ranks agree.
  do {
    const batch = await queryIndexer(`query TeamCount($cursor: BigInt!, $block: Int!) {
      saleAccounts(block: {number: $block}, where: {deposits_gt: "0", rankKey_gt: $cursor}, orderBy: rankKey, orderDirection: asc, first: 1000) { rankKey }
      ${META}
    }`, { cursor, block })
    batchSize = batch.saleAccounts.length
    total += batchSize
    if (batchSize) cursor = batch.saleAccounts[batchSize - 1].rankKey
  } while (batchSize === 1000)
  if (!total) return empty
  page = Math.min(Math.max(1, page), Math.ceil(total / pageSize))
  const data = await queryIndexer(`query Team($rankKey: BigInt!, $block: Int!, $first: Int!, $skip: Int!) {
    saleAccounts(block: {number: $block}, where: {deposits_gt: "0", rankKey_gt: $rankKey}, orderBy: rankKey, orderDirection: asc, first: $first, skip: $skip) {
      ${ACCOUNT_FIELDS}
    }
    ${META}
  }`, { rankKey, block, first: pageSize, skip: (page - 1) * pageSize })
  return {
    list: data.saleAccounts.map((item, index) => ({ ...member(item), rank: snapshot.saleStats.participantCount - total + (page - 1) * pageSize + index + 1 })),
    total, page, pageSize,
  }
}
